import React, { useState, useEffect } from 'react'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import Card from '@/shared/Card'
import Loading from '@/shared/Loading'
import { Link } from 'react-router-dom'

// Reads the Charge ledger only, which is the reconciled record of money that
// actually moved. The old `payments` and `payment_records` tables are frozen
// archives with known-wrong amounts and are never shown to anyone.
//
// Anything that didn't take money — an authorization we released, a failed
// card, an abandoned checkout — is filtered out server-side. Showing a user a
// "charge" they were never charged for is worse than showing nothing.

const money = (cents) => `$${(cents / 100).toFixed(2)}`

const STATUS_PILL = {
  succeeded: { label: 'Paid', className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  refunded: { label: 'Refunded', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  partially_refunded: { label: 'Partly refunded', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' },
  disputed: { label: 'Disputed', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  reversed: { label: 'Reversed', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
}

const StatusPill = ({ status }) => {
  const pill = STATUS_PILL[status] || { label: status, className: 'bg-gray-100 text-gray-700' }
  return (
    <span className={`text-xs font-medium px-2 py-1 rounded ${pill.className}`}>
      {pill.label}
    </span>
  )
}

const BillingHistory = () => {
  const [, setToast] = useGlobalState('toast')
  const { getRequest } = useQuery()

  const [loading, setLoading] = useState(true)
  const [charges, setCharges] = useState([])
  const [totalCents, setTotalCents] = useState(0)
  const [pendingSources, setPendingSources] = useState([])

  useEffect(() => {
    // getRequest(uri, params, callback) — callback style, and `params` is
    // required: it goes straight into Object.keys().
    getRequest('/api/v3/billing/history', {}, (err, data) => {
      setLoading(false)
      if (err || !data?.charges) {
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-red-100 text-red-600">Error</p>
          <p className="mt-1 text-sm dark:text-red-300 text-red-400">
            Couldn't dig up your receipts. Give it another go?
          </p>
        </div>)
        return
      }
      setCharges(data.charges)
      setTotalCents(data.total_cents)
      setPendingSources(data.pending_sources || [])
    })
  }, [])

  if (loading) return <Loading />

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Billing history</h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Thank you so much for your support in CCC! Seriously, every little bit goes a long way to keep the site alive!
      </p>

      {/* Shown to everyone, always. The cutoff is Charge::LIVE_FROM — keep the
          date here in step with that constant. */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-900 dark:text-blue-200">
          Billing history from before August 15th, 2026 isn't available here. If you need a
          receipt from before then, email support and we'll dig it out for you.
        </p>
      </div>

      {pendingSources.includes('subscription') && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-900 dark:text-yellow-200">
            <span className="font-medium">Membership payments aren't here yet.</span>{' '}
            We're still reconciling those and won't put numbers in front of you until we're
            sure they're right.
          </p>
        </div>
      )}

      {charges.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <p className="text-gray-900 dark:text-white font-medium mb-1">Nothing here yet.</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Buy some <Link to="/coins" className="text-cccblue hover:underline">coins</Link> or{' '}
              <Link to="/advertising/new" className="text-cccblue hover:underline">run an ad</Link> or get <Link to='/subscriptions' className="text-cccblue hover:underline">CCC Premium</Link> and
              this page gets more interesting.
            </p>
          </div>
        </Card>
      ) : (
        <>
          <Card>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {charges.map((charge) => (
                <div key={charge.id} className="py-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {charge.description}
                      </span>
                      <StatusPill status={charge.status} />
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {new Date(charge.occurred_at).toLocaleDateString(undefined, {
                        year: 'numeric', month: 'long', day: 'numeric',
                      })}
                      {charge.payment_method && <> · {charge.payment_method}</>}
                    </div>
                    {charge.refunded_cents > 0 && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Refunded {money(charge.refunded_cents)}
                        {charge.refunded_partially && ' of this charge'}
                      </div>
                    )}
                  </div>

                  <div className="text-right shrink-0">
                    <div className="font-bold text-gray-900 dark:text-white">
                      {money(charge.amount_cents)}
                    </div>
                    {charge.receipt_number && (
                      <Link
                        to={`/settings/billing/receipts/${charge.receipt_number}`}
                        className="text-sm text-cccblue hover:underline"
                      >
                        Receipt
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  )
}

export default BillingHistory
